/**
 * MONSTER TOWN — BUILD GHOST. OWNED BY THE BUILD CREW (src/build/**).
 *
 * "What you are about to place, exactly where it will land." The preview is the
 * whole safety net of build mode: an invalid placement simply shows red and
 * never commits — there is no error sound, no shake, no punishment (LAW #2).
 *
 * ============ THE PREVIEW SHOWS THE PIECE. IN ITS OWN COLOURS. ============
 * The version this replaces painted EVERY mesh of the piece with one flat mint
 * material at 15% opacity. Measured: with `block_wood` armed, the preview was a
 * translucent mint box with green corner posts — no shape, no colour, no
 * material of the actual piece, and the posts read as the arrows of a 3D
 * transform gizmo. A non-reader picking from a palette of unlabelled icons had
 * NOTHING telling them what was about to land. That is the whole point of a
 * preview, so:
 *
 *   1. every mesh keeps ITS OWN COLOUR, flattened and made translucent. A
 *      wooden block previews brown, a pink flower previews pink, a red roof
 *      previews red. Invalid mixes that colour toward red so "won't fit" is
 *      still read at a glance, without throwing the piece's identity away;
 *   2. it stays SEE-THROUGH and writes no depth, so a monster standing behind
 *      (or inside) the armed tile is never half-swallowed;
 *   3. a hard BRIGHT WIREFRAME traces the piece's edges — line work is what
 *      makes a translucent shape read as a diagram of a thing rather than a
 *      smudge, and it is what separates the preview from a monster on the same
 *      tile;
 *   4. a FLAT FOOTPRINT RING with FLAT CORNER PLATES marks the landing tile,
 *      drawn with depth testing OFF so it is visible through anything. This is
 *      the answer to the named critic defect "camera that hides what you're
 *      placing". The corner marks are FLAT ON THE GROUND on purpose: the
 *      vertical corner posts they replace pointed outward in four directions
 *      and read as a transform gizmo, which is the last thing a 3-year-old
 *      needs to see;
 *   5. a gentle bob, so the ghost never reads as an already-placed object.
 *
 * AND IT DOES NOT ARM THE TILE THE MONSTER IS STANDING ON. Entering build mode
 * used to point the preview at `app.playerPos`, so the first thing a kid saw was
 * their own monster (and often a neighbour) standing in the middle of the ghost,
 * hiding the piece completely. `openSpotNear()` arms clear ground a couple of
 * tiles away instead.
 * ==========================================================================
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { rotatedSize, type Cell, type Rot } from '../core/grid'
import { requirePlaceable } from '../core/placeable'
import { world, type SceneNode } from '../core/scene'
import type { PlaceableId, SceneId } from '../core/types'
import { engine } from '../core/engine'
import { app, type GameContext } from '../app/context'
import { hex } from '../core/palette'
import { boxGeo, group } from '../render/kit'
import { artContext } from './art'
import { landingFor } from './placer'
import { buildTool, carried, ghostDefId, selectionRot } from './state'

interface GhostView {
  root: THREE.Group
  piece: THREE.Object3D
  /** The landing pad, pulsed every frame so the landing tile catches the eye. */
  pad: THREE.Group | null
  sceneId: SceneId
  key: string
}

let view: GhostView | null = null
let lastPoint: { x: number; z: number } | null = null
let bob = 0
/** The camera, for turning the first-preview offsets with the view. */
let cameraRef: { rotation: Rot } | null = null

// ---------------------------------------------------------------------------
// materials — CACHED. The ghost is rebuilt on every finger move; allocating a
// material per rebuild would leak one GPU program per tile a kid drags over.
// ---------------------------------------------------------------------------

const matCache = new Map<string, THREE.Material>()

function cachedMat(key: string, make: () => THREE.Material): THREE.Material {
  const hit = matCache.get(key)
  if (hit !== undefined) return hit
  const m = make()
  matCache.set(key, m)
  return m
}

/** The faint body of the preview. No depth write: nothing behind it is eaten. */
function bodyMaterial(valid: boolean): THREE.Material {
  return cachedMat(`ghostBody:${valid ? 1 : 0}`, () =>
    new THREE.MeshBasicMaterial({
      color: hex(valid ? 'uiGhostOk' : 'uiGhostBad'),
      transparent: true,
      opacity: 0.15,
      depthWrite: false,
    }),
  )
}

/**
 * THE PIECE'S OWN COLOUR, held up to the light.
 *
 * Flat (MeshBasic, no lighting) so the preview never competes with a real
 * placed object, translucent so nothing behind it is lost, and keyed by colour
 * so a tree built out of six materials still only ever costs six cached
 * materials for the whole life of the game — the ghost is rebuilt on every
 * finger move and must not allocate.
 */
const white = new THREE.Color(0xffffff)

function ghostColorOf(src: THREE.Material | readonly THREE.Material[] | null): THREE.Color | null {
  const m = Array.isArray(src) ? src[0] : (src as THREE.Material | null)
  if (m === null || m === undefined) return null
  const c = (m as THREE.MeshStandardMaterial).color
  return c === undefined ? null : c
}

function pieceMaterial(base: THREE.Color, valid: boolean): THREE.Material {
  const key = `ghostPiece:${base.getHexString()}:${valid ? 1 : 0}`
  return cachedMat(key, () => {
    const c = base.clone()
    // Valid: a touch toward white so it reads as "not yet real".
    // Invalid: pushed toward the danger red, but never all the way — a kid must
    // still be able to tell WHICH piece will not fit.
    c.lerp(valid ? white : new THREE.Color(hex('uiGhostBad')), valid ? 0.06 : 0.55)
    return new THREE.MeshBasicMaterial({
      color: c,
      transparent: true,
      // Opaque enough that the colour is unmistakable at arm's length on a
      // small screen — at 0.6 the grass behind bled through a wooden block hard
      // enough to turn it grey — and still see-through, with no depth write, so
      // anything standing behind it is visible rather than swallowed.
      opacity: valid ? 0.82 : 0.6,
      depthWrite: false,
    })
  })
}

/** The wireframe that makes it read as a diagram of the piece. */
function edgeMaterial(valid: boolean): THREE.Material {
  return cachedMat(`ghostEdge:${valid ? 1 : 0}`, () =>
    new THREE.LineBasicMaterial({
      color: hex(valid ? 'uiOk' : 'uiDanger'),
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    }),
  )
}

/**
 * THE LANDING PAD. ALWAYS visible, through walls, monsters and everything else.
 *
 * ============ A GREEN RING ON GREEN GRASS IS NOT A SIGNAL (fixed r3) ============
 * Round-3 critic, blocker: "Ghost placement preview is invisible outdoors — pale
 * green on green grass … a faint mint-green translucent diamond with a barely-
 * visible outline sitting on saturated green grass … a ~90px smudge. This is the
 * single most important piece of feedback in build mode and it does not read."
 * And indoors the piece kept its own salmon colour, so valid-vs-invalid was not
 * readable there either.
 *
 * The colour of the world is not knowable in advance — grass is saturated green,
 * a living room floor is brown, a shop is cream — so the pad does not rely on
 * ONE colour reading against it. It is three layers:
 *
 *   CREAM PLATE  a near-solid pale slab under the piece. Cream is the one value
 *                that is lighter than grass, lighter than floorboards and
 *                darker than nothing — this is what makes the preview a bright
 *                shape on the ground instead of a smudge.
 *   INK EDGE     a dark band around the plate. Line work is what turns a patch
 *                of colour into a drawn object at arm's length on a small
 *                screen.
 *   YES/NO BAND  a bright green (or red) band inside the ink. The plate carries
 *                the visibility; this carries the ANSWER, and it reads the same
 *                on grass, on wood and on tile because it always sits on cream.
 *
 * It pulses gently, so on a still frame the eye is pulled to it.
 * ==============================================================================
 */
function markerMaterial(valid: boolean): THREE.Material {
  return cachedMat(`ghostMark:${valid ? 1 : 0}`, () =>
    new THREE.MeshBasicMaterial({
      color: hex(valid ? 'uiOk' : 'uiDanger'),
      transparent: true,
      opacity: 1,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    }),
  )
}

/** The dark band that makes the pad a drawn object rather than a stain. */
function padInkMaterial(): THREE.Material {
  return cachedMat('ghostPadInk', () =>
    new THREE.MeshBasicMaterial({
      color: hex('ink'),
      transparent: true,
      opacity: 0.85,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    }),
  )
}

/** The cream slab. THE reason the preview reads on saturated grass. */
function padPlateMaterial(): THREE.Material {
  return cachedMat('ghostPadPlate', () =>
    new THREE.MeshBasicMaterial({
      color: hex('flowerWhite'),
      transparent: true,
      opacity: 0.62,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    }),
  )
}

/** Where a carried piece was lifted FROM. Neutral, never red, never green. */
function homeMaterial(): THREE.Material {
  return cachedMat('ghostHome', () =>
    new THREE.MeshBasicMaterial({
      color: hex('uiAccent'),
      transparent: true,
      opacity: 0.9,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    }),
  )
}

// ---------------------------------------------------------------------------
// marker geometry — ring on the landing surface + shadow on the ground
// ---------------------------------------------------------------------------

function marker(w: number, h: number, d: number, mat: THREE.Material, order = 900): THREE.Mesh {
  const m = new THREE.Mesh(boxGeo(w, h, d), mat)
  m.castShadow = false
  m.receiveShadow = false
  // Drawn after the world so the depth-test-off marker lands on top of it.
  m.renderOrder = order
  m.frustumCulled = false
  return m
}

/**
 * The three-layer landing pad (see `markerMaterial` above): cream plate, ink
 * edge, yes/no band. Flat on the landing surface — never vertical posts, which
 * splay under the iso projection and read as the arrows of a transform gizmo.
 *
 * The middle stays open on purpose: a solid pad hides the piece standing in it,
 * which is the problem this whole file exists to avoid.
 */
function footprintRing(w: number, d: number, valid: boolean, y: number): THREE.Group {
  const g = group('ghostRing')
  g.name = 'ghostPad'

  // 1. the cream plate — the reason any of this is visible on grass.
  const plate = marker(w * 0.98, 0.05, d * 0.98, padPlateMaterial(), 896)
  plate.position.y = y
  g.add(plate)

  /** A hollow rectangle of four bars, `grow` world units bigger than the tile. */
  const ring = (thick: number, grow: number, mat: THREE.Material, order: number, lift: number): void => {
    const rw = w + grow
    const rd = d + grow
    const bars: [number, number, number, number][] = [
      [rw, thick, 0, -rd / 2 + thick / 2],
      [rw, thick, 0, rd / 2 - thick / 2],
      [thick, rd, -rw / 2 + thick / 2, 0],
      [thick, rd, rw / 2 - thick / 2, 0],
    ]
    for (const [bw, bd, bx, bz] of bars) {
      const bar = marker(bw, 0.06, bd, mat, order)
      bar.position.set(bx, y + lift, bz)
      g.add(bar)
    }
  }
  const unit = Math.max(0.12, Math.min(w, d))
  /*
   * 2. the ink edge, drawn OUTSIDE the tile, and 3. the yes/no band flush with
   * it. Order matters: with both flush the ink ended up drawn INSIDE the green
   * and the pad read as a hollow dark diamond with a green rim, which is line
   * work pointing the wrong way. Ink outside, colour inside, cream in the
   * middle — dark, bright, pale, in that order, on any background.
   */
  ring(unit * 0.14, unit * 0.16, padInkMaterial(), 898, 0.01)
  ring(unit * 0.16, 0, markerMaterial(valid), 900, 0.02)
  return g
}

/**
 * WHERE THE PIECE YOU ARE HOLDING CAME FROM.
 *
 * Round-3 critic, major: a 420 ms finger rest picks a placed piece up and "the
 * very next tap elsewhere on the ground moved its anchor … no confirmation, no
 * visible 'you are holding something' gate". This is half of the answer in the
 * world (the other half is the drawer's carry state and its PUT IT BACK button):
 * a teal home marker stays on the tile the piece was lifted from, so a kid can
 * always see both where it was and where it is about to go.
 */
function homeMarker(w: number, d: number): THREE.Group {
  const g = group('ghostHome')
  const unit = Math.max(0.12, Math.min(w, d))
  const thick = unit * 0.16
  const bars: [number, number, number, number][] = [
    [w, thick, 0, -d / 2 + thick / 2],
    [w, thick, 0, d / 2 - thick / 2],
    [thick, d, -w / 2 + thick / 2, 0],
    [thick, d, w / 2 - thick / 2, 0],
  ]
  for (const [bw, bd, bx, bz] of bars) {
    const bar = marker(bw, 0.06, bd, homeMaterial(), 899)
    bar.position.set(bx, 0.06, bz)
    g.add(bar)
  }
  return g
}

let homeView: { root: THREE.Group; sceneId: SceneId; key: string } | null = null

function clearHome(): void {
  if (homeView !== null && homeView.root.parent !== null) homeView.root.parent.remove(homeView.root)
  homeView = null
}

/** Keep the "lifted from here" marker in step with the carry. */
function refreshHome(): void {
  const carry = carried()
  if (carry === null) {
    clearHome()
    return
  }
  const node = world.get(carry.sceneId)
  if (node === undefined) {
    clearHome()
    return
  }
  const def = requirePlaceable(carry.defId)
  const key = `${carry.instanceId}|${carry.from.gx}|${carry.from.gy}|${carry.from.gz}`
  if (homeView !== null && homeView.key === key) return
  clearHome()
  const { w, d } = rotatedSize(def.footprint, carry.fromRot)
  const t = node.grid.tileSize
  const root = homeMarker(w * t, d * t)
  const c = node.grid.footprintCenter(carry.from, def.footprint, carry.fromRot)
  root.position.set(c.x, 0, c.z)
  node.actors.add(root)
  homeView = { root, sceneId: carry.sceneId, key }
}

const edgeGeos = new Map<string, THREE.BufferGeometry>()

/**
 * The wireframe crate around the piece. ONE draw call whatever the piece is,
 * and it is the single thing that stops the preview and a monster standing on
 * the same tile from reading as one blob: a hard box says "this is a preview of
 * an object that goes here", the fill inside it says "this is the shape".
 */
function pieceCrate(piece: THREE.Object3D, valid: boolean): THREE.LineSegments | null {
  const box = new THREE.Box3().setFromObject(piece)
  if (!isFinite(box.min.x) || box.isEmpty()) return null
  const size = new THREE.Vector3()
  const centre = new THREE.Vector3()
  box.getSize(size)
  box.getCenter(centre)
  const w = Math.max(0.12, size.x)
  const h = Math.max(0.12, size.y)
  const d = Math.max(0.12, size.z)
  const key = `${w.toFixed(2)}:${h.toFixed(2)}:${d.toFixed(2)}`
  let geo = edgeGeos.get(key)
  if (geo === undefined) {
    geo = new THREE.EdgesGeometry(new THREE.BoxGeometry(w, h, d))
    edgeGeos.set(key, geo)
  }
  const lines = new THREE.LineSegments(geo, edgeMaterial(valid))
  lines.position.copy(centre)
  lines.renderOrder = 880
  lines.name = 'ghostCrate'
  return lines
}

function groundShadow(w: number, d: number): THREE.Mesh {
  const m = new THREE.Mesh(
    boxGeo(w * 0.86, 0.04, d * 0.86),
    cachedMat('ghostShadow', () =>
      new THREE.MeshBasicMaterial({ color: hex('shadow'), transparent: true, opacity: 0.22 }),
    ),
  )
  m.position.y = 0.02
  m.castShadow = false
  m.receiveShadow = false
  return m
}

// ---------------------------------------------------------------------------
// build / clear
// ---------------------------------------------------------------------------

export function clearGhost(): void {
  if (view !== null && view.root.parent !== null) view.root.parent.remove(view.root)
  view = null
}

function buildGhost(
  node: SceneNode,
  defId: PlaceableId,
  anchor: Cell,
  rot: Rot,
  valid: boolean,
  key: string,
): void {
  clearGhost()
  const def = requirePlaceable(defId)
  const root = group('buildGhost')

  const piece = def.buildArt(artContext(node, defId, anchor, rot, null, true))
  const fallback = bodyMaterial(valid)
  /**
   * THROW THE INK SHELLS AWAY FIRST.
   *
   * `kit.inked()` wraps a mesh in a slightly larger BackSide copy painted with
   * the ink colour. Recolouring every mesh uniformly turned those shells into
   * near-opaque DARK boxes drawn around each part — a wooden block previewed as
   * a grey box, which is the reported defect wearing new paint. The preview does
   * not need ink: the bright wireframe crate is its line work, and dropping the
   * shells also halves the ghost's draw calls on the hand-me-down iPad.
   */
  const shells: THREE.Object3D[] = []
  piece.traverse((o) => {
    const m = o as THREE.Mesh
    if (!m.isMesh) return
    const mat = Array.isArray(m.material) ? m.material[0] : m.material
    if (mat !== undefined && (mat as THREE.Material).side === THREE.BackSide) shells.push(m)
  })
  for (const s of shells) s.parent?.remove(s)

  piece.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.isMesh) {
      // KEEP THE PIECE'S OWN COLOUR. This is the whole preview.
      const own = ghostColorOf(m.material)
      m.material = own === null ? fallback : pieceMaterial(own, valid)
      m.castShadow = false
      m.receiveShadow = false
      m.renderOrder = 850
    }
  })
  piece.rotation.y = (-rot * Math.PI) / 2
  root.add(piece)
  const crate = pieceCrate(piece, valid)
  if (crate !== null) root.add(crate)

  const { w, d } = rotatedSize(def.footprint, rot)
  const tile = node.grid.tileSize
  const surfaceY = 0.05
  const pad = footprintRing(w * tile, d * tile, valid, surfaceY)
  root.add(pad)
  // Where the piece is stacked, drop a hint square back down on the ground so
  // the kid can still see which column of the town they are working in.
  if (anchor.gy > 0) {
    const shadow = groundShadow(w * tile, d * tile)
    shadow.position.y = -node.grid.levelY(anchor.gy) + 0.02
    root.add(shadow)
  } else {
    root.add(groundShadow(w * tile, d * tile))
  }

  const c = node.grid.footprintCenter(anchor, def.footprint, rot)
  root.position.set(c.x, c.y, c.z)
  node.actors.add(root)
  view = { root, piece, pad, sceneId: node.def.id, key }
}

// ---------------------------------------------------------------------------
// public updates
// ---------------------------------------------------------------------------

/** Move the preview to the tile under this world point. */
export function updateGhostAt(sceneId: SceneId, groundX: number, groundZ: number): void {
  lastPoint = { x: groundX, z: groundZ }
  refreshGhost(sceneId)
}

/** Rebuild the preview where it already is (selection, rotation or grid changed). */
export function refreshGhost(sceneId?: SceneId): void {
  const id = sceneId ?? world.activeSceneId
  if (id === null) return
  const node = world.get(id)
  if (node === undefined) return

  refreshHome()

  const defId = ghostDefId()
  if (app.mode !== 'build' || buildTool() === 'delete' || defId === null || lastPoint === null) {
    clearGhost()
    return
  }

  const carry = carried()
  const rot = selectionRot()
  const landing =
    carry === null
      ? landingFor(node, defId, lastPoint.x, lastPoint.z, rot)
      : landingFor(node, defId, lastPoint.x, lastPoint.z, rot, carry.instanceId)
  const key = `${defId}|${landing.anchor.gx}|${landing.anchor.gy}|${landing.anchor.gz}|${rot}|${landing.valid ? 1 : 0}|${landing.swaps === null ? 0 : 1}`
  if (view !== null && view.key === key && view.sceneId === id) return

  buildGhost(node, defId, landing.anchor, rot, landing.valid, key)
  bus.emit('build:ghostMoved', { anchor: landing.anchor, rot, valid: landing.valid })
}

// ---------------------------------------------------------------------------
// where the preview points when nobody has aimed it yet
// ---------------------------------------------------------------------------

/**
 * Offsets tried in order, in cells, written for camera rotation 0 and TURNED
 * WITH THE CAMERA below. Never (0,0): arming the tile the monster is standing on
 * is what put the player — and a neighbour who happened to walk past — inside
 * the ghost, so the very first preview a kid ever saw was two characters and no
 * piece.
 *
 * ============ AND THEY ALL POINT UP THE SCREEN (fixed r3) ============
 * They used to be +X/+Z. Under the iso projection at rot 0 that is straight
 * DOWN the screen — into the build drawer. Measured indoors: "the salmon couch
 * ghost sits at y~830-960 with the build drawer starting at y~985", i.e. the
 * preview was pressed against the drawer edge, which is the named critic defect
 * "camera that hides what you're placing".
 *
 * Away from the camera is up the screen, and away from the camera at rot 0 is
 * -X/-Z. `turn()` rotates that with the camera, so the first preview lands in
 * clear screen space above the monster at every one of the four rotations.
 * ======================================================================
 */
const OPEN_SPOT_OFFSETS: readonly (readonly [number, number])[] = [
  [-2, -2], [-2, -1], [-1, -2], [-3, -2], [-2, -3], [-3, -1], [-1, -3],
  [-2, 0], [0, -2], [-3, -3], [-4, -2], [-2, -4],
]

/** One quarter turn of a cell offset, matching `Rot` (rot 1 sends +X to +Z). */
function turn(dx: number, dz: number, rot: Rot): [number, number] {
  let x = dx
  let z = dz
  for (let i = 0; i < rot; i++) {
    const nx = z
    const nz = -x
    x = nx
    z = nz
  }
  return [x, z]
}

/** Clear ground near a world point: in bounds, nothing already in the column. */
function openSpotNear(node: SceneNode, x: number, z: number): { x: number; z: number } {
  const grid = node.grid
  const from = grid.worldToCell(x, z, 0)
  const rot = cameraRef?.rotation ?? 0
  for (const [bx, bz] of OPEN_SPOT_OFFSETS) {
    const [dx, dz] = turn(bx, bz, rot)
    const cell = { gx: from.gx + dx, gy: 0, gz: from.gz + dz }
    if (!grid.inBounds(cell)) continue
    if (grid.at(cell) !== null) continue
    const w = grid.cellToWorld(cell)
    return { x: w.x, z: w.z }
  }
  return { x, z }
}

/** Where the ghost currently is, for the gesture layer. */
export function ghostPoint(): { x: number; z: number } | null {
  return lastPoint
}

export function setGhostPoint(p: { x: number; z: number } | null): void {
  lastPoint = p
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

function armNearPlayer(): { x: number; z: number } | null {
  const id = world.activeSceneId
  if (id === null) return null
  const node = world.get(id)
  if (node === undefined) return null
  return openSpotNear(node, app.playerPos.x, app.playerPos.z)
}

export function installGhost(ctx: GameContext): void {
  cameraRef = ctx.camera
  // Band 20-29 belongs to the build crew.
  engine.addSystem({
    id: 'build.ghost',
    order: 24,
    render: (_alpha, frameMs) => {
      if (view === null) return
      bob += frameMs / 1000
      view.piece.position.y = Math.sin(bob * 3.4) * 0.06 + 0.06
      // The pad breathes. On a still frame — which is every frame a kid is
      // deciding — a moving edge is what pulls the eye to the landing tile.
      const pad = view.pad
      if (pad !== null) {
        const s = 1 + Math.sin(bob * 3.4) * 0.045
        pad.scale.set(s, 1, s)
      }
    },
  })

  bus.on('app:modeChanged', (e) => {
    if (e.mode !== 'build') {
      clearGhost()
      return
    }
    // Entering build mode: arm CLEAR GROUND near the kid, so the very first
    // thing they see is a preview of the piece — not their own monster
    // standing inside it (see the header).
    if (lastPoint === null) lastPoint = armNearPlayer()
    refreshGhost()
  })

  bus.on('build:selected', () => refreshGhost())
  bus.on('build:rotated', () => refreshGhost())
  bus.on('build:toolChanged', () => refreshGhost())
  bus.on('build:placed', () => refreshGhost())
  bus.on('build:removed', () => refreshGhost())
  bus.on('build:undo', () => refreshGhost())
  bus.on('build:carryStart', () => refreshHome())
  bus.on('build:carryEnd', () => clearHome())
  bus.on('scene:left', () => {
    clearGhost()
    clearHome()
    lastPoint = null
  })
  // Walking into a room in build mode must arm a tile too, or the drawer shows a
  // selected piece and the world shows nothing at all.
  bus.on('scene:entered', () => {
    if (app.mode === 'build' && lastPoint === null) lastPoint = armNearPlayer()
    refreshGhost()
  })

  void ctx
}
